import { Box, IconButton, Slider } from "@mui/material";
import { DownloadSimple, Pause, Play } from "phosphor-react";
import { useEffect, useState } from "react";

const AudioControls = ({ audioUrl, audioRef }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);

  // Initialize audio and handle URL changes
  useEffect(() => {
    // Create new audio element
    const audio = new Audio(audioUrl);

    const handleMetadataLoaded = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (audio.currentTime && isFinite(audio.currentTime)) {
        setCurrentAudioTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentAudioTime(0);
    };

    // Add event listeners
    audio.addEventListener("loadedmetadata", handleMetadataLoaded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    // Load metadata
    audio.load();

    // Store reference
    audioRef.current = audio;

    // Cleanup function
    return () => {
      if (audio) {
        audio.pause();
        audio.removeEventListener("loadedmetadata", handleMetadataLoaded);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
        audio.src = "";
      }
      audioRef.current = null;
    };
  }, [audioUrl]);

  // Handle play/pause
  useEffect(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.play().catch((error) => {
        console.error("Error playing audio:", error);
        setIsPlaying(false);
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds || !isFinite(timeInSeconds) || isNaN(timeInSeconds)) {
      return "00:00";
    }

    const totalSeconds = Math.floor(timeInSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <IconButton onClick={() => setIsPlaying(!isPlaying)}>
        {isPlaying ? (
          <Pause size={24} color="#6C65D9" weight="fill" />
        ) : (
          <Play size={24} color="#6C65D9" weight="fill" />
        )}
      </IconButton>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "calc(100% - 100px)",
        }}
      >
        <Slider
          min={0}
          max={duration || 100}
          step={0.1}
          value={currentAudioTime || 0}
          onChange={(e, newValue) => {
            if (audioRef.current && isFinite(newValue)) {
              audioRef.current.currentTime = newValue;
              setCurrentAudioTime(newValue);
            }
          }}
          sx={{
            width: "100%",
            color: "#6C65D9",
            "& .MuiSlider-thumb": {
              top: "70%",
              left: "0.8px",
            },
          }}
        />
        <div
          style={{
            display: "flex",
            width: "100%",
            height: "10px",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: "12px" }}>
            {formatTime(currentAudioTime)}
          </span>
          <span style={{ fontSize: "12px" }}>
            -{formatTime(duration - (currentAudioTime || 0))}
          </span>
        </div>
      </div>
      <IconButton>
        <DownloadSimple size={24} color="#F9F9F9" />
      </IconButton>
    </Box>
  );
};

export default AudioControls;
